import { useState } from "react";

import {
  appendDownloadFormLink,
  defaultTemplate,
  isValidFormRedirectUrl,
  parseFormRedirectUrl,
} from "@smart/bridge-templates-basic";
import { useDebounce } from "@smart/itops-hooks-dom";
import { useAnalyticsEvent } from "@smart/manage-hooks-dom";

import { MessageSection } from "./message-section";
import {
  ConfirmationMode,
  ConfirmationModeToggle,
  RedirectUrlField,
} from "../../form-builder/edit-form-v2/heading/settings/confirmation-section";

type ConfirmationMessageProps = {
  confirmation?: string | null;
  redirectUrl?: string | null;
  update: (values: {
    confirmation?: string;
    redirectUrl?: string | null;
  }) => Promise<void>;
};

export const ConfirmationMessage = ({
  confirmation,
  redirectUrl,
  update,
}: ConfirmationMessageProps) => {
  const { publishEvent } = useAnalyticsEvent();
  const [mode, setMode] = useState<ConfirmationMode>(
    redirectUrl ? "redirect" : "message",
  );
  const [redirectUrlValue, setRedirectUrlValue] = useState(redirectUrl ?? "");

  const trackRedirectUrlAdded = () => {
    publishEvent?.({
      category: "track",
      name: "IntakeRedirectUrlAdded",
      screen: "FirmSettingsMessageTemplates",
      entryPoint: "IntakeSettings",
      component: "Confirmation Message",
      jiraKey: "LINK-3632",
      interactionType: "Active",
      properties: {
        scope: "Firm",
      },
    }).catch(console.error);
  };

  const saveRedirect = useDebounce((value: string) => {
    const trimmed = value.trim();
    if (!trimmed) {
      update({ redirectUrl: null }).catch(console.error);
      return;
    }
    const parsed = parseFormRedirectUrl(trimmed);
    if (parsed) {
      update({ redirectUrl: parsed })
        .then(trackRedirectUrlAdded)
        .catch(console.error);
    }
  }, 500);

  const onModeChange = (next: ConfirmationMode) => {
    setMode(next);
    if (next === "message") {
      update({ redirectUrl: null }).catch(console.error);
    } else if (isValidFormRedirectUrl(redirectUrlValue.trim())) {
      update({ redirectUrl: redirectUrlValue.trim() })
        .then(trackRedirectUrlAdded)
        .catch(console.error);
    }
  };

  return (
    <div className="flex flex-col gap-medium p-[1.6rem]">
      <ConfirmationModeToggle mode={mode} onModeChange={onModeChange} />
      {mode === "message" ? (
        <MessageSection
          description="This message will be shown after the form has been submitted."
          value={appendDownloadFormLink(
            confirmation?.trim() || defaultTemplate.response,
          )}
          fallback={defaultTemplate.response}
          save={(value) => update({ confirmation: value })}
          excludingFieldNames={[
            "clientName",
            "clientCustomName",
            "clientEmail",
            "formLink",
            "matterType",
          ]}
        />
      ) : (
        <RedirectUrlField
          redirectUrl={redirectUrlValue}
          onRedirectUrlChange={(value) => {
            setRedirectUrlValue(value);
            saveRedirect(value);
          }}
        />
      )}
    </div>
  );
};
